import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Archive,
  CardComponent,
  ConfirmationPopup,
  Contract,
  CustomTable,
  Edit,
  Exclamation,
  NoDataVisual,
  PageTitleAndSubTitle,
  Reports,
  Trash,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useState } from 'react';
import Snackbar from '../../_components/atoms/Snackbar';
import SummaryOfBenefitsConstants from '../../core-utils/messages/SummaryOfBenefitsConstants';
import Loader from '../../_components/molecules/Loader';
import { arrayToString } from '../../core-utils/helper-utils/helper';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import axios from 'axios';
import properties from '../../core-utils/properties';
import { observer } from 'mobx-react';
import { useStores } from '../../stores';
import { CustomTableColumns } from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/CustomTable';
import EditPlanDocsModal from '../../_components/organisms/EditPlanDocsModal';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { PLAN_DOCUMENTS_INTERNAL } from '../../core-utils/routes';
import { useNavigate } from 'react-router-dom';
import { getPlanDocReports } from '../../core-utils/helper-utils/contractAIQHelper';
import { updatePlan } from '../../core-utils/axios/compliance-assurance/planDocuments';
import { extractPlanDocsReport } from '../../core-utils/helper-utils/contractReportHelper';

const useStyles = makeStyles()({
  root: {
    padding: '40px 0px 66px 63px',
    height: '91vh',
    width: '100%',
  },
  clickableText: {
    textDecoration: `underline 2px dashed ${patientAccessTheme.palette.primary.main}`,
    textUnderlineOffset: '4px',
  },
});

const PlanDocuments = observer(() => {
  //constants
  const { classes } = useStyles();
  const messages = SummaryOfBenefitsConstants;
  const employerGroupStore = useStores().employerGroupStore;
  const egNid = employerGroupStore.employerGroupResponseData.eg_nid[0];
  const employer =
    employerGroupStore.employerGroupResponseData.employer_group_name[0];
  const navigate = useNavigate();
  const columns: CustomTableColumns[] = [
    {
      header: 'Benefit Types',
      accessor: 'benefitTypes',
      cell: (value: any, row) => (
        <Grid>
          <Typography
            fontSize="14px"
            lineHeight="22.4px"
            fontType="medium"
            textColor={patientAccessTheme.palette.primary.main}
            onClick={() => handleRowClick(row)}
            cursor="pointer"
            className={classes.clickableText}
          >
            {arrayToString(value)}
          </Typography>
        </Grid>
      ),
    },
    {
      header: 'Document Type',
      accessor: 'docType',
      width: '200px',
    },
    {
      header: 'Plan Names',
      accessor: 'planNames',
      cell: (value: any) => <>{arrayToString(value)}</>,
    },
    {
      header: 'Vendors',
      accessor: 'vendors',
      cell: (value: any) => <>{arrayToString(value)}</>,
    },
  ];
  const rowMenuItems = [
    {
      label: 'View Plan Document',
      icon: <Contract />,
    },
    {
      label: 'View Reports',
      icon: <Reports />,
    },
    {
      label: 'Edit Details',
      icon: <Edit color={patientAccessTheme.palette.text.highemphasis} />,
    },
    {
      label: 'Archive',
      icon: (
        <Archive
          width="22px"
          height="22px"
          fill={patientAccessTheme.palette.text.highemphasis}
        />
      ),
    },
    {
      label: 'Delete',
      icon: <Trash />,
    },
  ];

  //states
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [loader] = useState<any>(<Loader />);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState<boolean>(false);
  const [planDocuments, setPlanDocuments] = useState<any[]>([]);
  const [archivedPlanDocuments, setArchivedPlanDocuments] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState<any>();
  const [updatedPlan, setUpdatedPlan] = useState<any>();
  const [popupState, setPopupState] = useState({
    isOpen: false,
    action: '', // 'archive', 'deletePlanDoc', 'deleteDocument'
    title: '',
    subTitle: '',
    handleConfirm: () => {},
  });
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  //functions
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  //Functions
  const closePopup = () => {
    setPopupState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleRowClick = (row: any) => {
    navigate(PLAN_DOCUMENTS_INTERNAL, {
      state: {
        planNames: row?.planNames,
        planId: row?.id,
        docType: row?.docType,
      },
    });
  };

  const handleArchivePlanDoc = async (planId: string) => {
    await updatePlan(planId, { status: 'Archive' }, handleOpenSnackBar);
    closePopup();
  };

  // const handleDeletePlanDoc = async (documentId: string) => {
  //   await deletePlanDocument(documentId);
  //   closePopup();
  // };

  const handleDeletePlan = async (planId: string) => {
    await deletePlan(planId);
    closePopup();
  };

  const handleRowMenuItemClick = (row: any, menuItem: string) => {
    setSelectedPlan(row);
    setUpdatedPlan(row);
    switch (menuItem) {
      case 'View Plan Document':
        navigate(PLAN_DOCUMENTS_INTERNAL, {
          state: {
            planNames: row?.planNames,
            planId: row?.id,
            docType: row?.docType,
          },
        });
        break;
      case 'View Reports':
        navigate(PLAN_DOCUMENTS_INTERNAL, {
          state: {
            planNames: row?.planNames,
            planId: row?.id,
            docType: row?.docType,
            isReportsTab: true,
          },
        });
        break;
      case 'Edit Details':
        setIsEditPopupOpen(true);
        break;
      case 'Archive':
        setPopupState({
          isOpen: true,
          action: 'archive',
          title: messages.ARCHIVE_PLAN_DOC.TITLE,
          subTitle: messages.ARCHIVE_PLAN_DOC.SUB_TITLE,
          handleConfirm: () => handleArchivePlanDoc(row?.id),
        });
        break;
      case 'Delete':
        setPopupState({
          isOpen: true,
          action: 'deletePlan',
          title: messages.DELETE_PLAN_DOC.TITLE,
          subTitle: messages.DELETE_PLAN_DOC.SUB_TITLE,
          handleConfirm: () => handleDeletePlan(row?.id),
        });
        break;
      default:
        break;
    }
  };

  //Axios Calls
  // const deletePlanDocument = async (documentId: string) => {
  //   await axios
  //     .post(
  //       properties.aiqBaseUrl +
  //         properties.contractBaseUrl +
  //         properties.deletePlanDocument,
  //       {
  //         document_id: documentId,
  //       },
  //     )
  //     .then(async (response) => {
  //       if (response.status === 200) {
  //         handleOpenSnackBar(`Document deleted successfully`, 'success', 5000);
  //         apiCalls();
  //       }
  //     })
  //     .catch((error) => {
  //       handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
  //     });
  // };

  const deletePlan = async (id?: string) => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.deletePlan,
        { plan_id: id },
      )
      .then(async (response) => {
        if (response.status === 200) {
          handleOpenSnackBar(`Plan deleted successfully`, 'success', 5000);
          apiCalls();
        }
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      });
  };

  const getPlanDocuments = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getPlanDocs,
        {
          eg_nid: egNid,
        },
      )
      .then(async (res: any) => {
        if (res.status === 200) {
          const data = res?.data?.data || [];
          const formattedData = await Promise.all(
            data.map(async (item: any) => ({
              id: item?._id,
              benefitTypes:
                item?.benefit_types && item.benefit_types.length > 0
                  ? item.benefit_types
                  : ['Medical'],
              docType: item?.document_type,
              planNames: item?.plan_names ?? [],
              vendors:
                item?.vendors?.map((vendor: any) => vendor?.vendor_name) ?? [],
              subRows:
                item?.documents?.map((doc: any) => ({
                  docName: doc?.document_name,
                  docType: doc?.document_type,
                  effectiveDate: doc?.document_effective_date,
                })) ?? [],
              allReports: await getPlanDocReports(egNid, item?.document_type),
              reports: item?.reports ?? [],
            })),
          );
          setPlanDocuments(formattedData);
        }
      })
      .catch((error) => {
        handleOpenSnackBar(error, 'error', 5000);
        throw error;
      });
  };

  const getArchivedPlanDocs = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getPlanDocs,
        {
          eg_nid: egNid,
          isArchive: true,
        },
      )
      .then(async (res: any) => {
        if (res.status === 200) {
          const data = res?.data?.data || [];
          const formattedData = data?.map((item: any) => ({
            id: item?._id,
            benefitTypes:
              item?.benefit_types && item.benefit_types.length > 0
                ? item.benefit_types
                : ['Medical'],
            docType: item?.document_type,
            planNames: item?.plan_names ?? [],
            vendors: item?.vendors ?? [],
            subRows:
              item?.documents?.map((doc: any) => ({
                docName: doc?.document_name,
                docType: doc?.document_type,
                effectiveDate: doc?.document_effective_date,
              })) ?? [],
          }));
          setArchivedPlanDocuments(formattedData);
        }
      })
      .catch((error) => {
        handleOpenSnackBar(error, 'error', 5000);
        throw error;
      });
  };

  const apiCalls = async () => {
    await Promise.all([getPlanDocuments(), getArchivedPlanDocs()]);
    setPageLoading(false);
  };

  //useEffects
  useEffect(() => {
    if (!egNid) {
      return;
    }
    apiCalls();
  }, [egNid]);

  return (
    <>
      {pageLoading ? (
        loader
      ) : (
        <Grid className={classes.root}>
          <PageTitleAndSubTitle
            title={messages.title}
            subTitle={messages.subTitle}
            showDownloadButton={false}
          />
          <CardComponent
            title={''}
            subTitle={''}
            width="1182px"
            mainGridStyles={{
              marginTop: '32px',
            }}
            body={
              planDocuments?.length > 0 ? (
                <CustomTable
                  columns={columns}
                  subColumns={[
                    {
                      header: 'Doc Name',
                      accessor: 'docName',
                    },
                    {
                      header: 'Doc Type',
                      accessor: 'docType',
                    },
                    {
                      header: 'Effective Date',
                      accessor: 'effectiveDate',
                    },
                  ]}
                  rowsData={planDocuments}
                  showRowMenu={true}
                  rowMenuItems={rowMenuItems}
                  handleRowMenuItemClick={handleRowMenuItemClick}
                />
              ) : (
                <NoDataVisual
                  content="No Plan Documents"
                  width="100%"
                  height="300px"
                />
              )
            }
          />
          {archivedPlanDocuments?.length > 0 && (
            <CardComponent
              title={messages.ARCHIVED}
              mainGridStyles={{ marginTop: '32px' }}
              titleSubTitleMargin="12px"
              width="1182px"
              body={
                <CustomTable
                  columns={columns}
                  subColumns={[
                    {
                      header: 'Doc Name',
                      accessor: 'docName',
                    },
                    {
                      header: 'Doc Type',
                      accessor: 'docType',
                    },
                    {
                      header: 'Effective Date',
                      accessor: 'effectiveDate',
                    },
                  ]}
                  rowsData={archivedPlanDocuments}
                  showRowMenu={true}
                  rowMenuItems={[
                    {
                      label: 'Delete',
                      icon: <Trash />,
                    },
                  ]}
                  handleRowMenuItemClick={handleRowMenuItemClick}
                />
              }
            />
          )}
        </Grid>
      )}
      {isEditPopupOpen && (
        <EditPlanDocsModal
          open={isEditPopupOpen}
          handleCancel={() => setIsEditPopupOpen(false)}
          handleUpdate={async () => {
            const newUpdatedPlan = await updatePlan(
              updatedPlan?.id,
              {
                benefit_types: updatedPlan?.benefitTypes,
                plan_names: updatedPlan?.planNames,
                reports: updatedPlan?.reports,
              },
              handleOpenSnackBar,
            );
            setIsEditPopupOpen(false);

            if (newUpdatedPlan) {
              const existingPlanReports = selectedPlan?.reports;

              const newPlanReports = newUpdatedPlan?.reports;

              const reportsToProcess = newPlanReports?.filter(
                (report: string) => !existingPlanReports?.includes(report),
              );

              if (newUpdatedPlan?.merge_status === 'Merged') {
                const planDocUrl = newUpdatedPlan?.documents?.find(
                  (item: any) =>
                    item?.document_type === 'Base Document' ||
                    item?.document_type === 'Master Document',
                )?.document_url;

                const reportContractMap: Record<
                  string,
                  { agreementTypes: string[] }
                > = {
                  'Comparison with Administrative Service Agreement': {
                    agreementTypes: [
                      'Third-Party Administrator (TPA)',
                      'Administrative Services Only (ASO) Provider',
                    ],
                  },
                  'Gap Analysis': {
                    agreementTypes: ['Stop-Loss Insurance Provider'],
                  },
                  'Comparison with PBM Service Agreement': {
                    agreementTypes: ['Pharmacy Benefit Manager (PBM)'],
                  },
                };

                for (const report of reportsToProcess ?? []) {
                  let compareAgreementId = '';
                  let compareUrl = '';

                  const config = reportContractMap[report];
                  if (config) {
                    const response = await axios.post(
                      `${properties.aiqBaseUrl}${properties.contractBaseUrl}${properties.getContracts}`,
                      { eg_nid: egNid },
                    );
                    const allContracts = response?.data?.data ?? [];
                    const matchedContract = allContracts.find(
                      (cont: any) =>
                        config.agreementTypes.includes(
                          cont?.service_agreement_name,
                        ) && cont?.merge_status === 'Merged',
                    );

                    if (matchedContract) {
                      compareAgreementId = matchedContract?.contract_id;
                      compareUrl =
                        matchedContract?.documents?.find(
                          (item: any) =>
                            item?.document_type === 'Base Document' ||
                            item?.document_type === 'Master Document',
                        )?.document_url ?? '';
                    }
                  }

                  await extractPlanDocsReport(
                    newUpdatedPlan?._id,
                    planDocUrl,
                    report,
                    egNid,
                    compareAgreementId,
                    compareUrl,
                    employer,
                  );
                }
              }
            }
          }}
          selectedPlan={updatedPlan}
          setSelectedPlan={setUpdatedPlan}
          benefitTypeOptions={selectedPlan?.benefitTypes ?? []}
          planNameOptions={selectedPlan?.planNames ?? []}
          vendorOptions={selectedPlan?.vendors ?? []}
          reportOptions={selectedPlan?.allReports ?? []}
        />
      )}
      {popupState.isOpen && (
        <ConfirmationPopup
          handleCancel={closePopup}
          handleConfirm={popupState.handleConfirm}
          icon={<Exclamation width="38px" height="38px" />}
          title={popupState.title}
          subTitle={popupState.subTitle}
          buttonsType="error"
          width={popupState.action === 'deletePlanDoc' ? '544px' : undefined}
          height={popupState.action === 'deletePlanDoc' ? '280px' : undefined}
        />
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
});

export default PlanDocuments;
